<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import PostCard from './components/PostCard.vue';
import CommentsLayout from './CommentsLayout.vue';
import PostsHeader from './components/PostsHeader.vue';
import AITestChat from './components/AITestChat.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  headerTitle: {
    type: String,
    default: 'Publicações',
  },
  uiFlags: {
    type: Object,
    default: () => ({}),
  },
  postsList: {
    type: Array,
    default: () => [],
  },
  postType: {
    type: String,
    default: '',
  },
  initialTab: {
    type: String,
    default: 'Facebook',
  },
  initialFilter: {
    type: Object,
    default: () => ({
      actives: 'all',
      sort: wootConstants.POSTS_SORT_BY.NEWEST,
    }),
  },
  initialSearchWord: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'search',
  'postActiveToggled',
  'filterChange',
  'currentPage',
  'tabChange',
  'postDeleted',
  'refresh',
]);

const store = useStore();

const selectedTab = ref(props.initialTab || 'Facebook');
const selectedFilter = ref({
  actives: 'all',
  sort: wootConstants.POSTS_SORT_BY.NEWEST,
});
const selectedSearchWord = ref('');
const searchWord = ref('');
const postsAggregated = ref({
  facebook: {
    posts: [],
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    perPage: 12,
  },
  instagram: {
    posts: [],
    currentPage: 1,
    totalCount: 0,
    totalPages: 0,
    perPage: 12,
  },
});
const showAITestChat = ref(false);
const selectedPost = ref(null);
const showRemovePostModal = ref(false);
const deletedPostInfo = ref(null);
const isRemovingPost = ref(false);

const getTabKey = computed(() => {
  return selectedTab.value.toLowerCase();
});

const currentPosts = computed(() => {
  return postsAggregated.value[getTabKey.value]?.posts || [];
});

const hasNoPosts = computed(() => {
  return !postsAggregated.value[getTabKey.value]?.totalCount;
});

watch(
  () => props.postsList,
  newPosts => {
    if (newPosts) {
      postsAggregated.value = newPosts;
    }
  },
  { immediate: true }
);

watch(
  () => props.initialTab,
  newTab => {
    selectedTab.value = newTab;
  },
  { immediate: true }
);

watch(
  () => props.initialFilter,
  newFilter => {
    selectedFilter.value = newFilter;
  },
  { immediate: true }
);

watch(
  () => props.initialSearchWord,
  newSearchWord => {
    selectedSearchWord.value = newSearchWord;
  },
  { immediate: true }
);

onMounted(() => {
  selectedTab.value = props.initialTab;
});

const handleTestMode = data => {
  showAITestChat.value = data.showAITestChat ?? false;
  selectedPost.value = data.selectedPost ?? null;
};

const hideAITestChat = () => {
  showAITestChat.value = false;
  selectedPost.value = null;
};

const onToggleActive = postId => {
  emit('postActiveToggled', postId);
};

const onFilterChange = filter => {
  emit('filterChange', filter);
};

const onSearch = searchText => {
  searchWord.value = searchText;
  emit('search', searchText);
};

const onTabChange = tab => {
  selectedTab.value = tab;
  emit('tabChange', tab);
};

const onPageChange = page => {
  emit('currentPage', { page, tab: selectedTab.value });
};

const openDeletedPostModal = ({ postId, pageId }) => {
  deletedPostInfo.value = { postId, pageId };
  showRemovePostModal.value = true;
};

const closeRemovePostModal = () => {
  showRemovePostModal.value = false;
  deletedPostInfo.value = null;
};

const confirmRemovePost = async () => {
  if (!deletedPostInfo.value?.postId || !deletedPostInfo.value?.pageId) return;
  isRemovingPost.value = true;
  try {
    await store.dispatch('posts/deletePost', {
      pageId: deletedPostInfo.value.pageId,
      postId: deletedPostInfo.value.postId,
    });
    closeRemovePostModal();
    emit('postDeleted');
  } finally {
    isRemovingPost.value = false;
  }
};
</script>

<template>
  <CommentsLayout
    :is-loading="uiFlags.isFetching"
    loading-message="Buscando publicações..."
    :no-records-found="hasNoPosts"
    no-records-message="Nenhuma publicação encontrada"
  >
    <template #header>
      <PostsHeader
        :tabs="[
          { name: 'Facebook', count: postsAggregated?.facebook?.totalCount },
          { name: 'Instagram', count: postsAggregated?.instagram?.totalCount },
        ]"
        :header-title="headerTitle"
        feature-name="facebook-pages-posts"
        show-back-button="true"
        :initial-tab="selectedTab"
        :initial-filter="selectedFilter"
        :initial-search-word="selectedSearchWord"
        :list-loading="uiFlags.isFetching"
        @tab-change="onTabChange"
        @filter-change="onFilterChange"
        @search="onSearch"
        @refresh="emit('refresh')"
      />
    </template>

    <template #body>
      <div class="flex flex-col min-h-[calc(100vh-280px)]">
        <!-- Posts Grid - Responsive with modern spacing -->
        <section
          v-if="currentPosts.length > 0"
          class="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-5 lg:gap-6 p-4 sm:p-6 lg:p-8 overflow-visible"
        >
          <PostCard
            v-for="post in currentPosts"
            :key="post.id"
            :post="post"
            :post-type="postType"
            class="h-full"
            @toggle-active="onToggleActive"
            @test-mode="handleTestMode"
            @deleted-post="openDeletedPostModal"
          />
        </section>

        <!-- Empty State -->
        <div
          v-else-if="!uiFlags.isFetching && hasNoPosts"
          class="flex-1 flex flex-col items-center justify-center p-8 text-center"
        >
          <div
            class="w-20 h-20 mb-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
          >
            <fluent-icon
              icon="search"
              size="32"
              class="text-slate-400 dark:text-slate-500"
            />
          </div>
          <h3
            class="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2"
          >
            {{ $t('POSTS.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 max-w-md">
            {{ $t('POSTS.EMPTY_STATE.MESSAGE') }}
          </p>
        </div>

        <!-- Pagination - Modern footer style -->
        <div
          v-if="
            currentPosts.length > 0 &&
            postsAggregated[getTabKey]?.totalPages > 1
          "
          class="sticky bottom-0 mt-auto pt-4 [&_>_div]:before:!hidden"
        >
          <PaginationFooter
            :current-page="postsAggregated[getTabKey]?.currentPage"
            :total-items="postsAggregated[getTabKey]?.totalCount"
            :items-per-page="postsAggregated[getTabKey]?.perPage"
            @update:current-page="onPageChange"
          />
        </div>
      </div>
    </template>
  </CommentsLayout>

  <woot-modal v-model:show="showAITestChat" :on-close="hideAITestChat">
    <AITestChat :key="selectedPost?.id" :post="selectedPost" />
  </woot-modal>

  <woot-modal
    v-model:show="showRemovePostModal"
    :on-close="closeRemovePostModal"
  >
    <div class="flex flex-col gap-4 p-4">
      <h3 class="text-base font-semibold text-slate-900 dark:text-white">
        {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_TITLE') }}
      </h3>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_MESSAGE') }}
      </p>
      <div class="flex justify-end gap-2 mt-2">
        <button
          type="button"
          class="woot-button secondary"
          :disabled="isRemovingPost"
          @click="closeRemovePostModal"
        >
          {{ $t('COMMENTS.POST_DETAILS.DELETED_POST_CANCEL') }}
        </button>
        <button
          type="button"
          class="woot-button danger"
          :disabled="isRemovingPost"
          @click="confirmRemovePost"
        >
          {{ $t('COMMENTS.POST_DETAILS.REMOVE_POST') }}
        </button>
      </div>
    </div>
  </woot-modal>
</template>
